/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} Fallback used when the theme setting is unavailable */
  static #DEFAULT_MAX_PRODUCTS = 4;

  /**
   * The maximum number of products to store, driven by
   * Theme settings > Search > "Recently viewed count".
   * Falls back to 4 so the module still works if `Theme` has not been defined.
   * @returns {number}
   */
  static get #maxProducts() {
    if (typeof Theme === 'undefined') return this.#DEFAULT_MAX_PRODUCTS;

    const count = Number(Theme.settings?.recently_viewed_count);

    return Number.isFinite(count) && count > 0 ? count : this.#DEFAULT_MAX_PRODUCTS;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#maxProducts);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * Capped on read as well as on write, so lowering the "Recently viewed count"
   * setting takes effect immediately instead of waiting for the stored list to
   * be trimmed by the next product view.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    /** @type {string[]} */
    let stored;

    try {
      stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }

    return Array.isArray(stored) ? stored.slice(0, this.#maxProducts) : [];
  }
}
